import * as React from 'react';
import * as styles from './top-navigation.module.scss';
import logo from './logo.png'
import { useSiteMetadata } from '../../../queries/site-medatata';
import classNames from '../../../utilities/class-names';
import SfNewWindowIcon from '../icons/new-window';
import { SfSearchIcon } from '../icons/seach-icon';
import { navigate } from 'gatsby';
import { uri } from '../../../utilities/uri';
import Anchor from '../../anchor/anchor';
import { useLocation } from '../../../hooks/location';
import AiButton from '../../ai-button/ai-button';
import CaretDown from '../../icons/caret-down';
import SkeletonLoader from '../../skeleton-loader/skeleton-loader';

// Added noopener manually
/* eslint-disable react/jsx-no-target-blank */

const navItems = [
    { name: "Sitefinity CMS", url: "/documentation/sitefinity-cms" },
    { name: "Sitefinity Insight", url: "/documentation/sitefinity-cms/insight" },
    { name: "Sitefinity Cloud", url: "/documentation/sitefinity-cms/cloud" },
]

const TopNavigation = ({ hideMobileMenu }) => {
    const [searchText, setSearchText] = React.useState('');
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [mobileNavVisible, setMobileNavVisible] = React.useState(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const searchInputRef = React.useRef(null);
    const mobileSearchToggleRef = React.useRef(null);
    const navRef = React.useRef(null);
    const location = useLocation();
    const [versionsOpen, setVersionsOpen] = React.useState(false);
    const [versions, setVersions] = React.useState(null);

    const versionSelectorWrapRef = React.useRef(null);
    const versionsMobilePopoverRef = React.useRef(null);

    React.useEffect(() => {
        if (!versionsOpen) {
            return;
        }

        const abortController = new AbortController();
        document.addEventListener('click', (e) => {
            if (versionSelectorWrapRef.current?.contains(e.target) || versionsMobilePopoverRef.current?.contains(e.target)) {
                return;
            }

            setVersionsOpen(false);
        }, {
            signal: abortController.signal
        });


        return () => {
            abortController.abort();
        };
    }, [versionsOpen]);

    React.useEffect(() => {
        // Both body and html need the class because iOS Safari ignores overflow:hidden on body alone.
        // Setting it on html as well prevents the page from scrolling behind the open side panel on iOS.
        document.body.classList.toggle('sf-mobile-nav', mobileNavVisible);
        document.documentElement.classList.toggle('sf-mobile-nav', mobileNavVisible);
        return () => {
            document.body.classList.remove('sf-mobile-nav');
            document.documentElement.classList.remove('sf-mobile-nav');
        };
    }, [mobileNavVisible]);

    React.useEffect(() => setMobileNavVisible(false), [location.pathname]);
    React.useEffect(() => setSearchOpen(false), [location.pathname]);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchTextParam = params.get('q');

        let searchText = '';
        if (searchTextParam) {
            searchText = searchTextParam;
        }

        setSearchText(searchText);
    }, [location]);

    React.useEffect(() => { if (!searchOpen) setSearchText(''); }, [searchOpen]);

    React.useEffect(() => {
        if (!searchOpen) return;
        const handleClick = (e) => {
            if (!e.target.closest(`.${styles.searchForm}`) && !e.target.closest(`.${styles.mobileSearchDropdown}`) && !mobileSearchToggleRef.current?.contains(e.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [searchOpen]);

    const siteMetadata = useSiteMetadata();

    React.useEffect(() => {
        if (!mobileOpen) return;
        const handleClick = (e) => {
            if (!e.target.closest(`.${styles.header}`)) {
                setMobileOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [mobileOpen]);

    React.useEffect(() => {
        if (!versionsOpen || versions) {
            return;
        }

        const url = `${siteMetadata.versionsRoot}/versions.json`.replaceAll('//', '/');

        fetch(url).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch versions: ${res.status} ${res.statusText}`);
            }
            return res.json();
        }).then(data => {
            setVersions(data);
        });
    }, [versionsOpen, versions, siteMetadata.versionsRoot]);

    const onSearchSubmit = (e) => {
        e.preventDefault();

        if (!searchText) {
            return;
        }

        navigate(uri`/search?q=${searchText}`);
    };

    const onAskAiClick = () => {
        if (searchText) {
            navigate(uri`/search/ai-chat?q=${searchText}`);
            return;
        }

        navigate(uri`/search/ai-chat`);
    }

    const changeVersion = (version) => {
        setVersionsOpen(false);

        let pathname = location.pathname.toLowerCase();

        [...versions].reverse().forEach(v => {
            if (pathname.startsWith(v.pathPrefix.toLowerCase())) {
                pathname = pathname.substring(v.pathPrefix.length + 1);
                console.log(pathname);
            }
        });

        const newPrefix = `/${version.pathPrefix}/${pathname}${location.search}${location.hash}`.replaceAll(/\/+/g, '/');
        window.location.href = newPrefix;
    }

    return <>
        {mobileOpen && <button className={styles.overlay} aria-label="Close menu" onClick={() => setMobileOpen(false)} />}
        {versionsOpen && <button className={styles.overlay} aria-label="Close versions" onClick={() => setVersionsOpen(false)} />}
        {searchOpen && <button className={styles.searchOverlay} aria-label="Close search" onClick={() => setSearchOpen(false)} />}
        <header className={classNames(styles.header, "e2e-header")}>
            <div className={styles.headerContent}>
                <Anchor className={styles.logoWrap} href="/">
                    <img className={styles.logo} src={logo} alt="Logo" />
                    <span>DOCUMENTATION</span>
                </Anchor>
                <button
                    className={styles.navToggleBtn}
                    onClick={(e) => { setMobileOpen(o => !o); e.currentTarget.blur(); }}
                    aria-expanded={mobileOpen}
                    aria-label="Toggle product menu"
                >
                    <svg className={classNames(styles.chevron, mobileOpen && styles.chevronOpen)} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                        <path d="M7 10l5 5 5-5z" />
                    </svg>
                </button>
                <div className={classNames(styles.navItems, mobileOpen && styles.navItemsOpen)}>
                    <p className={styles.dropdownLabel}>SELECT PRODUCT DOCUMENTATION</p>
                    {
                        navItems.map(item => <React.Fragment key={item.name}>
                            <a onClick={() => setMobileOpen(false)} className={classNames(styles.navItem, siteMetadata.pathPrefix === item.url && styles.active)} href={item.url}>{item.name}</a>
                        </React.Fragment>)
                    }
                    <a onClick={() => setMobileOpen(false)} className={classNames(styles.navItem, styles.knowledgeBase)} href="https://community.progress.com/s/global-search/%40uri#t=KnowledgeBase&sort=relevancy&f:@commonproductgroup=[Sitefinity]" target="_blank" rel='noopener'>
                        <span>Knowledge Base</span>
                        <SfNewWindowIcon className={styles.kbIcon} />
                    </a>
                </div>
            </div>
        </header>
        <nav ref={navRef} className={classNames(styles.nav, "e2e-nav")}>
            <div className={styles.navContent}>
                <div className={styles.mobileMenuWrap}>
                    <button onClick={() => { setMobileNavVisible(v => !v); setSearchOpen(false); }} className={classNames(styles.mobileMenuBtn, hideMobileMenu && styles.mobileMenuBtnHidden)} aria-label='Menu'>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                            <path d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" />
                        </svg>
                    </button>
                    <div className={styles.productName}>{siteMetadata.productName}</div>
                </div>
                <div className='u-h100'>
                    {
                        siteMetadata.currentVersion && <>
                            <div ref={versionSelectorWrapRef} className={styles.versionSelectorWrap}>
                                <button
                                    className={classNames(styles.versionSelector, versionsOpen && styles.open)}
                                    onClick={() => {
                                        setVersionsOpen(o => !o);
                                        setMobileNavVisible(false);
                                        setSearchOpen(false);
                                    }}
                                    aria-haspopup="true"
                                    aria-expanded={versionsOpen}
                                    aria-label="Select version">
                                    <div className={styles.selectedVersion}>{siteMetadata.currentVersion}</div>
                                    <CaretDown className={styles.caretDown} />
                                </button>
                                {versionsOpen && <>
                                    <div className={styles.versionsPopover}>
                                        {versions?.map(v => <React.Fragment key={v.name}>
                                            <button className={classNames(styles.versionItem, v.name === siteMetadata.currentVersion && styles.active)} onClick={() => changeVersion(v)}>
                                                Version {v.name}
                                            </button>
                                        </React.Fragment>)}
                                        {!versions && <>
                                            <SkeletonLoader className={styles.versionLoader} widths={
                                                [
                                                    '80%',
                                                    '80%',
                                                    '80%',
                                                    '80%',
                                                    '80%',
                                                    '80%',
                                                    '80%',
                                                ]}>
                                            </SkeletonLoader>
                                        </>}
                                    </div>
                                </>}
                            </div>
                        </>
                    }
                </div>
                <div className="u-fg-1"></div>
                <button
                    ref={mobileSearchToggleRef}
                    className={classNames(styles.mobileSearchToggle, searchOpen && styles.searchActive)}
                    onClick={() => {
                        setSearchOpen(prev => {
                            if (!prev) {
                                setMobileNavVisible(false);
                                setVersionsOpen(false);
                                setTimeout(() => searchInputRef.current?.focus(), 50);
                            }
                            return !prev;
                        });
                    }}
                    aria-label="Search"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="16.5" y1="16.5" x2="22" y2="22" />
                    </svg>
                </button>
                <form className={styles.searchForm} onSubmit={onSearchSubmit}>
                    <button className={styles.searchButton} type="submit" aria-label="Search">
                        <SfSearchIcon />
                    </button>
                    <input value={searchText} onChange={e => setSearchText(e.target.value)} className={styles.searchInput} name="q" type="text" placeholder={`Search ${siteMetadata.productName} documentation...`} />
                    {/* needed so the gradient inside the svg works */}
                    {!searchOpen && <AiButton className={styles.aiSearchButton} onClick={onAskAiClick} aria-label="Ask AI">Ask AI</AiButton>}
                </form>
            </div>
            {searchOpen && (
                <form className={styles.mobileSearchDropdown} onSubmit={onSearchSubmit}>
                    <input ref={searchInputRef} value={searchText} onChange={e => setSearchText(e.target.value)} className={styles.mobileSearchInput} name="q" type="text" placeholder={`Search ${siteMetadata.productName} documentation...`} />
                    <AiButton className={styles.aiSearchButton} onClick={onAskAiClick} aria-label="Ask AI">Ask AI</AiButton>
                </form>
            )}
            {
                versionsOpen && <>
                    <div ref={versionsMobilePopoverRef} className={styles.mobileVersionsPopover}>
                        {versions?.map(v => <React.Fragment key={v.name}>
                            <button className={classNames(styles.versionItem, v.name === siteMetadata.currentVersion && styles.active)} onClick={() => changeVersion(v)}>
                                Version {v.name}
                            </button>
                        </React.Fragment>)}
                        {!versions && <>
                            <SkeletonLoader className={styles.versionLoader} widths={
                                [
                                    '80%',
                                    '80%',
                                    '80%',
                                    '80%',
                                    '80%',
                                    '80%',
                                    '80%',
                                ]}>
                            </SkeletonLoader>
                        </>}
                    </div>
                </>
            }
        </nav>
    </>
}

export default TopNavigation;